import React from 'react';
import { AppProps } from 'next/app';
import { ContainerModule, Container } from 'inversify';
import isServer from '@framework/router/Next/isServer';
import { createContainer, PageRenderingStateValue } from '@framework/di';
import { TYPES } from '@framework/config/DI/types';
import { loadModules } from '@framework/di/loadModules';

type WithContainerProps = AppProps & {
  container: Container,
};

export const configureApp = (
  Component: React.ComponentType<WithContainerProps>,
  modules: ContainerModule[] = [],
) => {
  // On Server Side we need to recreate container for each request
  // On Client Side we need to remember container in the closure.
  let clientContainer: Container | null = null;

  if (!isServer()) {
    clientContainer = createContainer(PageRenderingStateValue.ClientSideRendering);
    loadModules(clientContainer, modules);
  }

  return (props: AppProps) => {
    if (props.pageProps?.method === 'HEAD') return null;

    const container = isServer()
      ? createContainer(PageRenderingStateValue.ServerSideRendering)
      : clientContainer!;

    if (!container.isBound(TYPES.Hostname)) {
      container.bind<string>(TYPES.Hostname).toConstantValue(props.pageProps?.hostname ?? '');
    }

    if (!container.isBound(TYPES.Locale)) {
      container.bind<string>(TYPES.Locale).toConstantValue(props.pageProps?.locale ?? '');
    }

    if (!container.isBound(TYPES.Nonce)) {
      container.bind<string>(TYPES.Nonce).toConstantValue(props.pageProps?.nonce ?? '');
    }

    if (isServer()) {
      loadModules(container, modules);
    }

    return <Component container={ container } { ...props } />;
  };
};
